/**
 * Seeded PRNG. Determinism matters: validation tests and benchmark runs
 * must be reproducible from a seed.
 *
 * Generator: mulberry32 (Tommy Ettinger, 2017, public domain). 32-bit state,
 * period 2^32. Adequate for visual Monte Carlo and test fixtures; not for
 * cryptography or for very long statistical runs.
 *
 * Normals: Box-Muller transform. Box, G. E. P. and Muller, M. E. (1958).
 * A Note on the Generation of Random Normal Deviates. Annals of
 * Mathematical Statistics, 29(2), 610-611. Each transform yields two
 * independent normals; the second is cached for the next call.
 */
export class Rng {
  private state: number;
  private cachedNormal = 0;
  private hasCachedNormal = false;

  constructor(seed: number) {
    this.state = seed >>> 0;
  }

  /** Uniform in [0, 1). */
  next(): number {
    let t = (this.state = (this.state + 0x6d2b79f5) >>> 0);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }

  /** Uniform in [lo, hi). */
  range(lo: number, hi: number): number {
    return lo + (hi - lo) * this.next();
  }

  /** Standard normal N(0, 1). */
  normal(): number {
    if (this.hasCachedNormal) {
      this.hasCachedNormal = false;
      return this.cachedNormal;
    }
    // 1 - next() is in (0, 1], so log never sees 0.
    const u1 = 1 - this.next();
    const u2 = this.next();
    const mag = Math.sqrt(-2 * Math.log(u1));
    const angle = 2 * Math.PI * u2;
    this.cachedNormal = mag * Math.sin(angle);
    this.hasCachedNormal = true;
    return mag * Math.cos(angle);
  }

  reseed(seed: number): void {
    this.state = seed >>> 0;
    this.hasCachedNormal = false;
  }
}
